import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { formatters } from '@morpho-org/ui-v6/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { vaultV2DefaultTimeFrame } from '@/config/table';
import { getParsedValue } from '@/helpers/formatters';
import { getVaultV2Apy } from '@/hooks/useVaultV2Apy';
import type { VaultV2ApyOverviewData } from '../VaultV2ApyOverview.mapper';
import { apyHistoryQueryOptions, toHistoryRange } from '../apyHistory.query';
interface UseVaultV2ApyChartDataProps {
  apyOverview: VaultV2ApyOverviewData;
  vaultAddress: Address;
  chainId: ChainId;
}
export interface UseVaultV2ApyChartDataReturn {
  timeFrame: Time.PeriodLike | null;
  setTimeFrame: (timeFrame: Time.PeriodLike | null) => void;
  formattedData: [number, number | null][];
  formattedPeriodAvgNetApy: string;
  formattedNetApy: string;
  netApy: number | null | undefined;
  loading: boolean;
  error: boolean;
}

/**
 * The V2 APY chart's series from `vaultV2.apyHistory`, and the current net APY from the overview.
 * The server loads the default range; picking another shows the chart skeleton while it loads, as
 * the Apollo query did, and a failed range shows the chart's error state.
 */
export function useVaultV2ApyChartData(t0) {
  const $ = _c(31);
  const {
    apyOverview,
    vaultAddress,
    chainId
  } = t0;
  const [timeFrame, setTimeFrame] = useState(vaultV2DefaultTimeFrame);
  let t1;
  if ($[0] !== chainId || $[1] !== timeFrame || $[2] !== vaultAddress) {
    t1 = apyHistoryQueryOptions({
      chainId,
      address: vaultAddress,
      range: toHistoryRange(timeFrame)
    });
    $[0] = chainId;
    $[1] = timeFrame;
    $[2] = vaultAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      ...t1,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    isFetching,
    isPlaceholderData,
    isPending,
    isError
  } = useQuery(t2);
  let formattedData;
  let formattedPeriodAvgNetApy;
  let t3;
  if ($[6] !== apyOverview.address || $[7] !== apyOverview.asset || $[8] !== apyOverview.managementFee || $[9] !== apyOverview.maxApy || $[10] !== apyOverview.netApy || $[11] !== apyOverview.netApyExcludingRewards || $[12] !== apyOverview.performanceFee || $[13] !== apyOverview.rewards || $[14] !== data?.averageNetApy || $[15] !== data?.points) {
    const {
      netApy: computedNetApy
    } = getVaultV2Apy({
      vaultV2: {
        address: apyOverview.address,
        asset: apyOverview.asset,
        performanceFee: apyOverview.performanceFee,
        managementFee: apyOverview.managementFee,
        netApyExcludingRewards: apyOverview.netApyExcludingRewards,
        netApy: apyOverview.netApy,
        maxRate: apyOverview.maxApy,
        rewards: apyOverview.rewards
      }
    });
    let t4;
    if ($[19] !== data?.points) {
      t4 = formatTimeseriesData(data?.points.map(_temp));
      $[19] = data?.points;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    formattedData = t4;
    let t5;
    if ($[21] !== data?.averageNetApy) {
      t5 = formatters.percent.default("-").digits(2).max(10).of(getParsedValue(data?.averageNetApy, safeParseNumber), 18);
      $[21] = data?.averageNetApy;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    formattedPeriodAvgNetApy = t5;
    t3 = formatters.percent.default("-").digits(2).max(10).of(computedNetApy, 18);
    $[6] = apyOverview.address;
    $[7] = apyOverview.asset;
    $[8] = apyOverview.managementFee;
    $[9] = apyOverview.maxApy;
    $[10] = apyOverview.netApy;
    $[11] = apyOverview.netApyExcludingRewards;
    $[12] = apyOverview.performanceFee;
    $[13] = apyOverview.rewards;
    $[14] = data?.averageNetApy;
    $[15] = data?.points;
    $[16] = formattedData;
    $[17] = formattedPeriodAvgNetApy;
    $[18] = t3;
  } else {
    formattedData = $[16];
    formattedPeriodAvgNetApy = $[17];
    t3 = $[18];
  }
  const formattedNetApy = t3;
  const t4 = data?.averageNetApy;
  const t5 = isPending || isFetching && isPlaceholderData;
  let t6;
  if ($[23] !== formattedData || $[24] !== formattedNetApy || $[25] !== formattedPeriodAvgNetApy || $[26] !== isError || $[27] !== t4 || $[28] !== t5 || $[29] !== timeFrame) {
    t6 = {
      timeFrame,
      setTimeFrame,
      formattedData,
      formattedPeriodAvgNetApy,
      formattedNetApy,
      netApy: t4,
      loading: t5,
      error: isError
    };
    $[23] = formattedData;
    $[24] = formattedNetApy;
    $[25] = formattedPeriodAvgNetApy;
    $[26] = isError;
    $[27] = t4;
    $[28] = t5;
    $[29] = timeFrame;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}
function _temp(t0) {
  const {
    timestamp,
    netApy
  } = t0;
  return [timestamp, netApy];
}